import { useQuery } from '@apollo/client'
import type { PromptResolve } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { format, isSameDay } from 'date-fns'
import { useMemo, useState } from 'react'
import { Button, Combobox, TimePicker } from '@/components'
import type { ComboboxItem } from '@/components/combobox'
import { FacilityCombobox } from '@/components/combobox/facility-combobox'
import VehicleSelect from '@/components/vehicle-select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import {
  ALL_DEPARTMENTS_QUERY,
  ALL_SALARY_CODES_QUERY,
  Module,
  ModuleOptionsValue,
} from '@/modules/companies'
import type { ExtendUserSalaryWorkTime } from './types.graphql'
import {
  ALL_VEHICLES_SELECT_QUERY,
  type AllVehiclesQueryVariables,
  type AllVehiclesSelectQuery,
  type VehicleSelectNode,
} from '@/modules/vehicles'
import { useModuleConfig, useNodes, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { WORK_PERIOD } from './queries'
import type { ApproveWorkPeriodNode } from './types.graphql'
import {
  getWorkPeriodDayExtensionEntries,
  getWorkPeriodDayExtensionPreviewRange,
  getWorkPeriodDayExtensionState,
  isWorkPeriodDayExtensionWithinLimit,
  type WorkPeriodDayExtensionEntry,
} from './work-period-day-extension'
import { getMostUsedVehicleFromActivities } from './work-period-most-used-vehicle'

export type ExtendWorkPeriodFormValues = {
  userId: string
  entries: WorkPeriodDayExtensionEntry[]
  salaryCodeId: string | null
  facility: string | null
  department: string | null
  comment: string
}

type ExtendWorkPeriodModalProps = {
  onResolve: PromptResolve<ExtendWorkPeriodFormValues | null>
  salaryWorkTime: ExtendUserSalaryWorkTime
  workPeriod: ApproveWorkPeriodNode
}

const formatRange = (start: Date, end: Date) => {
  if (isSameDay(start, end)) {
    return `${format(start, 'dd.MM HH:mm')} - ${format(end, 'HH:mm')}`
  }

  return `${format(start, 'dd.MM HH:mm')} - ${format(end, 'dd.MM HH:mm')}`
}

const formatBoundary = (value: Date) => format(value, 'dd.MM HH:mm')

export const ExtendWorkPeriodModal: React.FC<ExtendWorkPeriodModalProps> = ({
  onResolve,
  salaryWorkTime,
  workPeriod,
}) => {
  const translations = useTranslate({
    extendWorkPeriod: 'salary.extend-work-period',
    description: 'salary.extend-work-period-description',
    preview: 'salary.extend-work-period-preview',
    currentDay: 'salary.extend-work-period-current',
    updatedDay: 'salary.extend-work-period-updated',
    maxDurationError: 'salary.extend-work-period-max-duration',
    from: 'common.from',
    until: 'common.until',
    vehicle: 'common.vehicle',
    salaryCode: 'common.salary-code',
    facility: 'common.facility',
    department: 'common.department',
    comment: 'common.comment',
    submit: 'common.submit',
    cancel: 'common.cancel',
    required: 'common.required',
    selectSalaryCode: 'components.combobox.salary-code.placeholder',
  })

  const { getModuleOptionStatus } = useModuleConfig(Module.TIMESHEETS)
  const { hasPermissions } = usePermissions()

  const departmentMandatory = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_DEPARTMENT_SELECT_REQUIRED
  )
  const facilityMandatory = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_FACILITY_SELECT_REQUIRED
  )
  const commentRequired = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_COMMENT_REQUIRED
  )
  const projectActive = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_PROJECTS
  )
  const salaryCodeActive = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_SALARY_CODE_IN_REGISTRATION
  )
  const canSetSalaryCodeManually = hasPermissions(
    PERMISSIONS.timesheets.overrideTimeEntrySalaryCode,
    false
  )
  const showSalaryCodeField = salaryCodeActive && canSetSalaryCodeManually

  const workPeriodStart = useMemo(
    () => new Date(workPeriod.datetimeStart),
    [workPeriod.datetimeStart]
  )
  const workPeriodEnd = useMemo(
    () => new Date(workPeriod.datetimeEnd),
    [workPeriod.datetimeEnd]
  )
  const extensionState = useMemo(
    () =>
      getWorkPeriodDayExtensionState({
        workPeriodStart,
        workPeriodEnd,
        timeEntries: workPeriod.timeEntries,
      }),
    [workPeriod.timeEntries, workPeriodEnd, workPeriodStart]
  )

  const [beforeTime, setBeforeTime] = useState(extensionState.currentStart)
  const [afterTime, setAfterTime] = useState(extensionState.currentEnd)
  const [salaryCodeId, setSalaryCodeId] = useState('')
  const [facilityId, setFacilityId] = useState('')
  const [departmentId, setDepartmentId] = useState(
    salaryWorkTime.defaultDepartment?.id ?? ''
  )
  const [vehicleId, setVehicleId] = useState('')
  const [comment, setComment] = useState('')

  const previewRange = useMemo(
    () =>
      getWorkPeriodDayExtensionPreviewRange({
        currentStart: extensionState.currentStart,
        currentEnd: extensionState.currentEnd,
        startTimeOfDay: beforeTime,
        endTimeOfDay: afterTime,
      }),
    [
      afterTime,
      beforeTime,
      extensionState.currentEnd,
      extensionState.currentStart,
    ]
  )
  const extensionEntries = useMemo(
    () =>
      getWorkPeriodDayExtensionEntries({
        currentStart: extensionState.currentStart,
        currentEnd: extensionState.currentEnd,
        startTimeOfDay: beforeTime,
        endTimeOfDay: afterTime,
      }),
    [
      afterTime,
      beforeTime,
      extensionState.currentEnd,
      extensionState.currentStart,
    ]
  )

  const { data: salaryCodeData } = useQuery(ALL_SALARY_CODES_QUERY, {
    skip: !showSalaryCodeField,
    variables: {
      timeEntryCodes: true,
    },
  })
  const { data: departmentsData } = useQuery(ALL_DEPARTMENTS_QUERY)
  const salaryCodes = useNodes(salaryCodeData?.allSalaryCodes)
  const companyDepartments = useNodes(departmentsData?.allDepartments)
  const salaryCodeItems = useMemo<ComboboxItem<string>[]>(
    () =>
      salaryCodes.map(code => ({
        value: code.id,
        label: code.name,
      })),
    [salaryCodes]
  )
  const availableDepartmentItems = useMemo<ComboboxItem<string>[]>(() => {
    const seen = new Set<string>()
    const salaryWorkTimeDepartments = [
      ...(salaryWorkTime.availableDepartments ?? []),
      ...(salaryWorkTime.defaultDepartment
        ? [salaryWorkTime.defaultDepartment]
        : []),
    ]
    const departments =
      salaryWorkTimeDepartments.length > 0
        ? salaryWorkTimeDepartments
        : companyDepartments

    return departments.reduce<ComboboxItem<string>[]>((acc, department) => {
      if (!department || seen.has(department.id)) return acc
      seen.add(department.id)
      acc.push({
        value: department.id,
        label: department.name,
      })
      return acc
    }, [])
  }, [
    companyDepartments,
    salaryWorkTime.availableDepartments,
    salaryWorkTime.defaultDepartment,
  ])

  const allowedDepartmentIds = useMemo(
    () => new Set(availableDepartmentItems.map(item => item.value)),
    [availableDepartmentItems]
  )

  const showDepartmentField =
    availableDepartmentItems.length > 0 || departmentMandatory
  const showVehicleField = showDepartmentField || projectActive

  // Prefill the vehicle (and through it the facility/department) with the
  // vehicle the driver used the most across the work period's activities.
  const { data: workPeriodActivitiesData } = useQuery(WORK_PERIOD, {
    skip: !showVehicleField,
    variables: { id: workPeriod.id },
  })
  // Kept in sync with VehicleSelect's own filtering so the prefill can never
  // resolve a vehicle that is missing from the dropdown.
  const { data: vehiclesData } = useQuery<
    AllVehiclesSelectQuery,
    AllVehiclesQueryVariables
  >(ALL_VEHICLES_SELECT_QUERY, {
    skip: !showVehicleField,
    variables: { orderBy: 'registrationNumber', currentlyOwned: true },
  })

  const prefillVehicle = useMemo(() => {
    const mostUsed = getMostUsedVehicleFromActivities(
      workPeriodActivitiesData?.workPeriod?.activities
    )
    if (!mostUsed) return null

    const vehicles = (vehiclesData?.allVehicles.edges ?? []).map(
      edge => edge.node
    )
    const vin = mostUsed.vehicleVin?.trim()
    const registrationNumber = mostUsed.vehicleRegistrationNumber?.trim()

    return (
      vehicles.find(vehicle => vin && vehicle.vin?.trim() === vin) ??
      vehicles.find(
        vehicle =>
          registrationNumber &&
          vehicle.registrationNumber?.trim() === registrationNumber
      ) ??
      null
    )
  }, [
    workPeriodActivitiesData?.workPeriod?.activities,
    vehiclesData?.allVehicles,
  ])

  const formErrors = useMemo(() => {
    const errors: {
      comment?: string
      department?: string
      facility?: string
      duration?: string
    } = {}

    if (
      !isWorkPeriodDayExtensionWithinLimit({
        start: previewRange.start,
        end: previewRange.end,
      })
    ) {
      errors.duration = translations.maxDurationError
    }

    if (showDepartmentField && departmentMandatory && !departmentId) {
      errors.department = translations.required
    }

    if (projectActive && facilityMandatory && !facilityId) {
      errors.facility = translations.required
    }

    if (commentRequired && !comment.trim()) {
      errors.comment = translations.required
    }

    return errors
  }, [
    comment,
    commentRequired,
    departmentId,
    departmentMandatory,
    facilityId,
    facilityMandatory,
    previewRange.end,
    previewRange.start,
    projectActive,
    showDepartmentField,
    translations.maxDurationError,
    translations.required,
  ])

  const formValid =
    extensionEntries.length > 0 &&
    Object.values(formErrors).every(error => !error)

  const handleVehicleChange = (vehicle: VehicleSelectNode | null) => {
    setVehicleId(vehicle?.id ?? '')

    if (!vehicle) return

    if (vehicle.facility?.id) {
      setFacilityId(vehicle.facility.id)
    }

    if (
      vehicle.department?.id &&
      allowedDepartmentIds.has(vehicle.department.id)
    ) {
      setDepartmentId(vehicle.department.id)
    }
  }

  // Apply the most-used-vehicle prefill once the queries resolve, without
  // clobbering any manual edit the user has already made. Adjusting state
  // during render (rather than in an effect) avoids a cascading re-render.
  const [prefillApplied, setPrefillApplied] = useState(false)
  if (!prefillApplied && prefillVehicle) {
    setPrefillApplied(true)
    setVehicleId(prefillVehicle.id)

    if (prefillVehicle.facility?.id) {
      setFacilityId(prefillVehicle.facility.id)
    }

    if (
      prefillVehicle.department?.id &&
      allowedDepartmentIds.has(prefillVehicle.department.id)
    ) {
      setDepartmentId(prefillVehicle.department.id)
    }
  }

  const handleSubmit = () => {
    if (!formValid) return

    onResolve({
      userId: workPeriod.user.id,
      entries: extensionEntries,
      salaryCodeId: salaryCodeId || null,
      facility: facilityId || null,
      department: departmentId || null,
      comment: comment.trim(),
    })
  }

  return (
    <Dialog open onOpenChange={open => !open && onResolve(null)}>
      <DialogContent className="flex max-h-[calc(100vh-2rem)] flex-col overflow-hidden p-0 sm:max-w-3xl">
        <DialogHeader className="shrink-0 px-4 pt-5 sm:px-6 sm:pt-6">
          <DialogTitle>{translations.extendWorkPeriod}</DialogTitle>
          <DialogDescription>{translations.description}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 pb-4 sm:px-6 sm:pb-6">
          <div className="grid gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Field data-invalid={!!formErrors.duration}>
                <FieldLabel>{translations.from}</FieldLabel>
                <TimePicker value={beforeTime} onChange={setBeforeTime} />
                <FieldDescription>
                  {formatBoundary(previewRange.start)}
                </FieldDescription>
              </Field>

              <Field data-invalid={!!formErrors.duration}>
                <FieldLabel>{translations.until}</FieldLabel>
                <TimePicker value={afterTime} onChange={setAfterTime} />
                <FieldDescription>
                  {formatBoundary(previewRange.end)}
                </FieldDescription>
              </Field>
            </div>

            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">
                {translations.preview}
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {translations.currentDay}
                  </div>
                  <div className="mt-1 text-sm text-gray-900">
                    {formatRange(
                      extensionState.currentStart,
                      extensionState.currentEnd
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {translations.updatedDay}
                  </div>
                  <div className="mt-1 text-sm font-medium text-gray-900">
                    {formatRange(previewRange.start, previewRange.end)}
                  </div>
                </div>
              </div>

              {!!formErrors.duration && (
                <p className="mt-3 text-sm text-red-700">
                  {formErrors.duration}
                </p>
              )}
            </div>

            <FieldGroup>
              <Field data-invalid={!!formErrors.comment}>
                <FieldLabel>{translations.comment}</FieldLabel>
                <Textarea
                  value={comment}
                  rows={4}
                  onChange={value => setComment(value)}
                />
                <FieldError>{formErrors.comment}</FieldError>
              </Field>

              {showVehicleField && (
                <Field>
                  <FieldLabel>{translations.vehicle}</FieldLabel>
                  <VehicleSelect
                    mode="single"
                    value={vehicleId}
                    clearable
                    onChange={handleVehicleChange}
                  />
                </Field>
              )}

              {showSalaryCodeField && (
                <Field>
                  <FieldLabel>{translations.salaryCode}</FieldLabel>
                  <Combobox
                    mode="single"
                    value={salaryCodeId}
                    items={salaryCodeItems}
                    clearable
                    placeholder={translations.selectSalaryCode}
                    onChange={value => setSalaryCodeId(value)}
                  />
                </Field>
              )}

              {showDepartmentField && (
                <Field data-invalid={!!formErrors.department}>
                  <FieldLabel>{translations.department}</FieldLabel>
                  <Combobox
                    mode="single"
                    value={departmentId}
                    items={availableDepartmentItems}
                    clearable={!departmentMandatory}
                    onChange={value => setDepartmentId(value)}
                  />
                  <FieldError>{formErrors.department}</FieldError>
                </Field>
              )}

              {projectActive && (
                <Field data-invalid={!!formErrors.facility}>
                  <FieldLabel>{translations.facility}</FieldLabel>
                  <FacilityCombobox
                    mode="single"
                    value={facilityId}
                    clearable={!facilityMandatory}
                    onChange={value => setFacilityId(value)}
                  />
                  <FieldError>{formErrors.facility}</FieldError>
                </Field>
              )}
            </FieldGroup>
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t px-4 py-4 sm:px-6">
          <Button variant="outline" size="lg" onClick={() => onResolve(null)}>
            {translations.cancel}
          </Button>
          <Button size="lg" disabled={!formValid} onClick={handleSubmit}>
            {translations.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
